import moment from "moment";
import { postToAgents } from "./PlaybookApi";
import { RESULT_SEGMENTS, STARTED_CHOICES, FINDINGS_CHOICES, VIEW_CHOICES } from "../constants/PlaybookResults";

const EXCEPTION_SEGMENT_KEYS = ["worth_a_look", "did_not_finish", "skipped", "in_progress"];
const DEFAULT_RESULT_FILTERS = {
	searchTerm: "",
	segment: "",
	teamId: "",
	personId: "",
	periodEnd: "",
	started: "",
	startedFrom: "",
	startedTo: "",
	findingsLevel: "",
	startedByUserId: "",
	bookedByUserId: "",
	playbookId: "",
	waitingOnMe: "",
	view: "latest",
	sort: "newest",
	page: 1,
};
const RESULTS_PAGE_SIZE = 50;

const PlaybookResultsService = {
	/**
	 * One page of per-client runs for the given result filters, with outcome counts for the
	 * filtered scope. Resolves to { runs, total, summary }.
	 *
	 * @param {Object} params
	 * @param {Object} params.filters result filters, as made by filtersFromQuery
	 * @param {Number} [params.clientId] only this client's runs
	 * @param {Number} [params.pageSize]
	 * @return {Promise<Object>}
	 */
	listRuns({ filters, clientId, pageSize = RESULTS_PAGE_SIZE }) {
		const { startedFrom, startedTo } = this.startedRangeFor({ filters });
		const body = {
			clientId,
			searchTerm: filters.searchTerm,
			teamId: filters.teamId,
			personId: filters.personId,
			playbookId: filters.playbookId,
			periodEnd: filters.periodEnd,
			startedByUserId: filters.startedByUserId,
			bookedByUserId: filters.bookedByUserId,
			startedFrom,
			startedTo,
			waitingOnMe: filters.waitingOnMe === "yes",
			view: filters.view,
			segment: filters.segment,
			findingsLevel: filters.findingsLevel,
			sort: filters.sort,
			limit: pageSize,
			offset: (Number(filters.page || 1) - 1) * pageSize,
		};
		return postToAgents({ operationId: "listPlaybookRuns", body });
	},

	/**
	 * The choices the result filters offer: teams, people on clients, agents, periods and who
	 * started runs and who booked their journals, each taken from the firm's own runs.
	 *
	 * @return {Promise<Object>} { teams, people, agents, periods, startedBy, bookedBy }
	 */
	listFilterOptions() {
		return postToAgents({ operationId: "listPlaybookRunFilterOptions", body: {} });
	},

	/**
	 * The choices for the results' Show filter. The everyday choice, latest run per client, has an
	 * empty value so the filter reads as not narrowing; viewFromFilterChoice turns it back.
	 *
	 * @return {Array} [{ value, label }]
	 */
	viewFilterChoices() {
		const choices = VIEW_CHOICES.map(choice => (choice.value === DEFAULT_RESULT_FILTERS.view ? { ...choice, value: "" } : choice));
		return choices;
	},

	/**
	 * The Show filter's value for a set of result filters, empty for the everyday view.
	 *
	 * @param {Object} params
	 * @param {Object} params.filters
	 * @return {String}
	 */
	viewFilterChoiceFor({ filters }) {
		const isEverydayView = filters.view === DEFAULT_RESULT_FILTERS.view;
		const choice = isEverydayView ? "" : filters.view;
		return choice;
	},

	/**
	 * The view a Show filter choice stands for.
	 *
	 * @param {Object} params
	 * @param {String} params.choice
	 * @return {String}
	 */
	viewFromFilterChoice({ choice }) {
		const view = choice || DEFAULT_RESULT_FILTERS.view;
		return view;
	},

	/**
	 * The result filters with nothing narrowed: latest run per client, newest first, page one.
	 *
	 * @return {Object}
	 */
	defaultResultFilters() {
		return { ...DEFAULT_RESULT_FILTERS };
	},

	/**
	 * Result filters read from a page address's query string, so a filtered list can be
	 * bookmarked, shared, and returned to from a run. Anything missing takes its default.
	 *
	 * @param {Object} params
	 * @param {String} params.search e.g. "?team=14&segment=to_review"
	 * @return {Object}
	 */
	filtersFromQuery({ search }) {
		const query = new URLSearchParams(search || "");
		const filters = { ...DEFAULT_RESULT_FILTERS };
		Object.keys(DEFAULT_RESULT_FILTERS).forEach(key => {
			if (query.has(key)) filters[key] = query.get(key);
		});
		filters.page = Math.max(1, Number(filters.page) || 1);
		return filters;
	},

	/**
	 * The query string for a set of result filters, leaving out every filter at its default.
	 *
	 * @param {Object} params
	 * @param {Object} params.filters
	 * @return {String} "" or "?key=value&…"
	 */
	queryFromFilters({ filters }) {
		const query = new URLSearchParams();
		Object.keys(DEFAULT_RESULT_FILTERS).forEach(key => {
			const value = filters[key];
			const isDefault = String(value === undefined || value === null ? "" : value) === String(DEFAULT_RESULT_FILTERS[key]);
			if (!isDefault) query.set(key, value);
		});
		const queryText = query.toString();
		return queryText ? `?${queryText}` : "";
	},

	/**
	 * How many filters narrow the list beyond the defaults, not counting the outcome strip,
	 * the sort or the page.
	 *
	 * @param {Object} params
	 * @param {Object} params.filters
	 * @return {Number}
	 */
	countNarrowingFilters({ filters }) {
		const narrowingKeys = [
			"searchTerm",
			"teamId",
			"personId",
			"periodEnd",
			"started",
			"findingsLevel",
			"startedByUserId",
			"bookedByUserId",
			"playbookId",
			"waitingOnMe",
			"view",
		];
		const count = narrowingKeys.filter(key => String(filters[key] || "") !== String(DEFAULT_RESULT_FILTERS[key])).length;
		return count;
	},

	/**
	 * The exact moments a "started" choice covers, in the viewer's own time zone, as ISO
	 * timestamps. A custom range covers whole days, the first from midnight and the last to
	 * the end of the day.
	 *
	 * @param {Object} params
	 * @param {Object} params.filters { started, startedFrom, startedTo }
	 * @return {Object} { startedFrom, startedTo } either may be null
	 */
	startedRangeFor({ filters }) {
		const now = moment();
		const ranges = {
			today: [moment().startOf("day"), now],
			last_7_days: [moment().subtract(6, "days").startOf("day"), now],
			last_30_days: [moment().subtract(29, "days").startOf("day"), now],
			this_month: [moment().startOf("month"), now],
			last_month: [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")],
		};
		if (filters.started === "custom") {
			const startedFrom = filters.startedFrom ? moment(filters.startedFrom).startOf("day").toISOString() : null;
			const startedTo = filters.startedTo ? moment(filters.startedTo).endOf("day").toISOString() : null;
			return { startedFrom, startedTo };
		}
		const range = ranges[filters.started];
		if (!range) return { startedFrom: null, startedTo: null };
		return { startedFrom: range[0].toISOString(), startedTo: range[1].toISOString() };
	},

	/**
	 * The result groups of the results strip, in order: where each run is in decisions and
	 * sign-off, or why it needed none. They do not overlap and together make up "All"; each
	 * group's key is also its count's key in the list summary ("" is the total).
	 *
	 * @return {Array} [{ key, label, tone }]
	 */
	resultSegments() {
		return RESULT_SEGMENTS;
	},

	/**
	 * Whether a result group only matters when something is in it — runs that are under way,
	 * could not do everything, or did not finish. The everyday groups always show.
	 *
	 * @param {Object} params
	 * @param {String} params.segment
	 * @return {Boolean}
	 */
	isExceptionSegment({ segment }) {
		return EXCEPTION_SEGMENT_KEYS.includes(segment);
	},

	/**
	 * The one-line movement under a result group's count: for the groups that wait on people,
	 * how many have waited over a week (the number that matters when thousands of clients run
	 * each month); otherwise how many arrived since yesterday.
	 *
	 * @param {Object} params
	 * @param {String} params.segment "" for all runs
	 * @param {Object} params.summary the page summary from listRuns
	 * @return {Object|null} { text, tone }
	 */
	describeSegmentDelta({ segment, summary }) {
		if (!summary) return null;
		const isWaitingGroup = segment === "needs_decisions" || segment === "waiting_on_signoff";
		const olderThanAWeek = isWaitingGroup && summary.olderThanAWeek ? summary.olderThanAWeek[segment] || 0 : 0;
		if (olderThanAWeek > 0) return { text: `${olderThanAWeek} waiting over a week`, tone: "warning" };
		const sinceYesterday = summary.sinceYesterday ? summary.sinceYesterday[segment || "total"] || 0 : 0;
		if (sinceYesterday > 0) return { text: `+${sinceYesterday} since yesterday`, tone: "neutral" };
		const delta = { text: "None since yesterday", tone: "neutral" };
		return delta;
	},

	/**
	 * Where a client's own page is: the type (corporate, personal, trust) is the route's
	 * first segment, so a client found by search must carry its type.
	 *
	 * @param {Object} params
	 * @param {Object} params.client { id, type }
	 * @return {String}
	 */
	clientPagePath({ client }) {
		const path = `/clients/${client.type}/${client.id}`;
		return path;
	},

	/**
	 * How many runs one page of results shows.
	 *
	 * @return {Number}
	 */
	resultsPageSize() {
		return RESULTS_PAGE_SIZE;
	},

	/**
	 * The fixed choices for the started and findings filters.
	 *
	 * @return {Object} { started, findings }
	 */
	fixedFilterChoices() {
		return { started: STARTED_CHOICES, findings: FINDINGS_CHOICES };
	},
};

export default PlaybookResultsService;
